import singleSpaHtml from 'single-spa-html';
import template from './topbar.html';
import { getAppConfig } from '../../utilities/api';
import buildTranslator from '../../utilities/translator';
import renderLanguageSelectMenu from './components/languageSelectMenu';
import renderUserMenuFlyout from './components/userMenuFlyout';
import renderUserMenuButton from './components/userMenuButton';
import renderLoginAndRegisterButtons from './components/loginAndRegisterButtons';

const htmlLifeCycles = singleSpaHtml({
    template,
});

const rootId = `#single-spa-application\\:topbar`;

/**
 * Appends an active class to the topbar root to help detect viewport
 * height settings while descendent apps have deprecated topbar style code.
 * @param {boolean} isActive Whether the topbar will be rendered or not.
 * @returns void.
 */
const _toggleRootActive = (isActive) => {
    const rootElement = document.querySelector(`${rootId}`);

    if (!rootElement) {
        return;
    }

    rootElement.classList.toggle('c-topbar--is-active', isActive);
};

/**
 * Sets the text for the sidebar toggle.
 * @param {Translator} translator Our translation tool.
 * @returns void.
 */
const _setSidebarToggleText = (translator) => {
    const sidebarToggleTextElement = document.getElementById('sidebar-toggle-text');
    if (!sidebarToggleTextElement) { return; }
    sidebarToggleTextElement.innerText = translator.translate('menu');
}

/**
 * Extends the default mount lifecycle function to set button visibility
 * and wire up event listeners and handlers.
 * @param {*} props Extra options passed in during application registration.
 * @returns {Promise<void>} A void promise indicating the work is complete.
 */
export const mount = async (props) => {
    await htmlLifeCycles.mount(props);
    _toggleRootActive(true);
    const { appSlug, appRootUrl, localesFromDatabase } = props;
    const appConfig = await getAppConfig(appRootUrl, appSlug);
    const translator = await buildTranslator(appConfig, 'topbar/locales', localesFromDatabase);

    const locales = localesFromDatabase?.length > 0 ? localesFromDatabase : appConfig?.localization?.locales;
    renderLanguageSelectMenu(locales);
    renderUserMenuFlyout(translator);
    renderUserMenuButton();
    await renderLoginAndRegisterButtons(appConfig, appSlug, translator);
    _setSidebarToggleText(translator);
};

/**
 * Extends the default unmount lifecycle function for teardown activities.
 * @param {*} props Extra options passed in during application registration.
 * @returns {Promise<void>} A void promise indicating the work is complete.
 */
export const unmount = async (props) => {
    await htmlLifeCycles.unmount(props);
    _toggleRootActive(false);
};

export const { bootstrap } = htmlLifeCycles;
